import { ReceiptPercentIcon } from '@heroiconsv2/react/24/outline'
import { formatPrice } from 'lib/utils'
import {
  getBookingCoupon,
  getBookingDiscountBreakdown,
  getBookingTotalSummary,
  getCouponCode,
  getCouponTypeTranslation,
  getCouponValueDisplay,
  getAjarleeServiceFee,
  getInsuranceDisplay,
  getInsuranceTypeLabel,
} from '../bookingDetailHelpers'
import BookingDiscountedAmount from './BookingDiscountedAmount'
import InsuranceOfficeNote from './InsuranceOfficeNote'
import { Band, MoneyRow, Panel, SectionCard } from './BookingSection'

const BookingOrderSummaryCard = ({ bookingData, t, isCompanyRole, hasAdminAccess, isPartnerOnly = false }) => {
  const usesCompanyAmounts = isCompanyRole && !hasAdminAccess && !isPartnerOnly
  const coupon = getBookingCoupon(bookingData)
  const ajarleeServiceFee = getAjarleeServiceFee(bookingData)
  const insurance = getInsuranceDisplay(bookingData)
  const insuranceTypeLabel = getInsuranceTypeLabel(bookingData, t)
  const discounts = getBookingDiscountBreakdown(bookingData)
  const totalSummary = getBookingTotalSummary(bookingData)

  const omr = value => `${formatPrice(value || 0, true)} ${t('common.omr')}`

  return (
    <SectionCard icon={ReceiptPercentIcon} title={t('booking.sections.orderSummary')}>
      <Panel className="py-3">
        <MoneyRow label={t('booking.orderSummary.priceWithoutTax')}>
          <BookingDiscountedAmount discount={discounts.daily} t={t} />
        </MoneyRow>

        {coupon && (
          <MoneyRow
            label={
              <>
                {t('booking.orderSummary.discount')}
                <span className="ml-1 rtl:ml-0 rtl:mr-1 text-slate-500">
                  ({getCouponCode(coupon)} &middot; {getCouponTypeTranslation(t, coupon)})
                </span>
              </>
            }
          >
            <span className="font-semibold text-emerald-600">{getCouponValueDisplay(t, coupon)}</span>
          </MoneyRow>
        )}

        <MoneyRow label={t('booking.orderSummary.depositAmount')}>{omr(bookingData?.depositAmount)}</MoneyRow>

        <MoneyRow label={t('booking.orderSummary.deliveryFee')}>
          <BookingDiscountedAmount discount={discounts.delivery} t={t} />
        </MoneyRow>

        {/* Insurance paid with the booking is part of `companyTotal`, so the company view must show the row too. */}
        {insurance.show && !insurance.collectAtOffice && (
          <MoneyRow
            label={
              <>
                {t('booking.orderSummary.insuranceFee')}
                {insurance.hasSplitType && insuranceTypeLabel && <span className="ml-1 rtl:ml-0 rtl:mr-1 text-slate-500">({insuranceTypeLabel})</span>}
              </>
            }
          >
            {omr(insurance.displayAmount)}
          </MoneyRow>
        )}

        {(bookingData?.extras || []).map(extra => (
          <MoneyRow key={extra.key} label={t(`booking.orderSummary.extra_${extra.key}`)}>
            {omr(extra.amount)}
          </MoneyRow>
        ))}

        <MoneyRow label={t('booking.orderSummary.vat')}>{omr(usesCompanyAmounts ? bookingData?.companyVat : bookingData?.vatTax)}</MoneyRow>

        {/* Keep the row when a discount zeroed the fee, so the original + source stay visible. */}
        {!usesCompanyAmounts && (ajarleeServiceFee > 0 || discounts.service.hasDiscount) && (
          <MoneyRow label={t('booking.orderSummary.serviceFees')}>
            <BookingDiscountedAmount discount={discounts.service} t={t} />
          </MoneyRow>
        )}

        <Band label={t('booking.orderSummary.total')}>
          {usesCompanyAmounts ? omr(bookingData?.companyTotal) : <BookingDiscountedAmount discount={totalSummary} t={t} valueClassName="font-bold" />}
        </Band>

        {!usesCompanyAmounts && <InsuranceOfficeNote bookingData={bookingData} t={t} />}
      </Panel>
    </SectionCard>
  )
}

export default BookingOrderSummaryCard
